import { useEffect, useRef, useState } from "react";
import { History, Paperclip, Send, UploadCloud, X } from "lucide-react";
import { type Chapter, type Settings, DEFAULT_SETTINGS, getChapters, getSettings, logEvent } from "../data";
import { type AuthUser, fetchMe, logout } from "../authClient";
import {
  type AskQuestion,
  type ConversationSummary,
  deleteAllConversations,
  deleteConversation,
  type ExportFormat,
  extractPdfText,
  extractDocxText,
  getConversation,
  getTurnStatus,
  listConversations,
  type ProjectInfo,
  startThread,
  streamMessage,
  type StreamMessageResult,
  type TurnProgress,
  watchProjectAnalysis,
  watchTurn,
} from "../chatClient";
import LoginPage from "../auth/LoginPage";
import DocumentEditor from "./DocumentEditor";
import { QuestionForm } from "./QuestionForm";
import { AccountMenu } from "./AccountMenu";
import { HistoryModal } from "./HistoryModal";
import { Sidebar } from "./Sidebar";
import { TypingDots } from "./TypingDots";
import {
  type Attachment,
  ASSISTANT_HISTORY_PLACEHOLDER,
  chapterDoneLabel,
  chapterGeneratingLabel,
  composeSendText,
  splitStoredUserText,
} from "./chatHelpers";

export default function App() {
  const [status, setStatus] = useState<"loading" | "anon" | "authed">("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    fetchMe().then((u) => {
      setUser(u);
      setStatus(u ? "authed" : "anon");
    });
  }, []);

  if (status === "loading") {
    return <div className="auth-loading">加载中…</div>;
  }

  if (status === "anon" || !user) {
    return (
      <LoginPage
        title="登录"
        subtitle="登录后即可开始对话"
        onSuccess={(u) => {
          setUser(u);
          setStatus("authed");
        }}
      />
    );
  }

  return (
    <ChatApp
      user={user}
      onLogout={async () => {
        await logout();
        setUser(null);
        setStatus("anon");
      }}
      onUpdateUser={setUser}
    />
  );
}

type Msg =
  | {
      id: number;
      kind: "assistant";
      /** 气泡正文：模型的正式答复，或生成中/已定稿的状态提示。 */
      text: string;
      /** 过程信息（模型边做边说的进度说明），折叠展示，不当作结果。 */
      process?: string;
      streaming?: boolean;
      questions?: AskQuestion[];
      answered?: boolean;
    }
  | { id: number; kind: "user"; text: string; attachments?: string[] }
  | { id: number; kind: "error"; text: string };

let nextId = 1;

function ChatApp({
  user,
  onLogout,
  onUpdateUser,
}: {
  user: AuthUser;
  onLogout: () => void;
  onUpdateUser: (user: AuthUser) => void;
}) {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  // 章节列表是异步加载的，而恢复会话/轮询进度的回调可能比它先跑起来，
  // 所以查章节标题一律走 ref，避免闭包里拿到还是空数组的旧值。
  const chaptersRef = useRef<Chapter[]>([]);
  chaptersRef.current = chapters;
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [threadStatus, setThreadStatus] = useState<"connecting" | "ready" | "failed">("connecting");
  const threadIdRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const attachmentIdRef = useRef(1);
  const [busy, setBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("docx");
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionLoggedRef = useRef(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [viewingLanding, setViewingLanding] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docModalChapter, setDocModalChapter] = useState<Chapter | null>(null);
  const [chapterIndex, setChapterIndex] = useState(0);
  // 已真正产出正文的章节编号，由服务端按 document_sections 给出。
  // 不能用 chapterIndex 推断完成状态：指针越过一个没写出正文的章节会显示假的"已完成"。
  const [completedChapters, setCompletedChapters] = useState<string[]>([]);
  const started = messages.length > 0 && !viewingLanding;
  const [analysisDone, setAnalysisDone] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [analysisNotice, setAnalysisNotice] = useState<string | null>(null);

  async function runProjectAnalysis(threadId: string) {
    setAnalyzing(true);
    try {
      const result = await watchProjectAnalysis(threadId);
      if (result.ok && result.projectInfo) {
        setProjectInfo(result.projectInfo);
      } else {
        // badge/name 只是侧边栏的展示性摘要，提取失败不影响已经产出的正文，
        // 提示一下即可，不再清空当前对话。
        setAnalysisNotice(result.error ?? "未能分析出有效的项目信息，不影响继续对话");
      }
    } catch (err) {
      setAnalysisNotice(err instanceof Error ? err.message : "项目信息分析失败，不影响继续对话");
    } finally {
      setAnalysisDone(true);
      setAnalyzing(false);
      refreshConversations();
    }
  }

  function refreshConversations() {
    listConversations().then(setConversations).catch(() => {});
  }

  function updateConversationUrl(id: string | null) {
    const url = new URL(window.location.href);
    if (id) {
      url.searchParams.set("conversation", id);
    } else {
      url.searchParams.delete("conversation");
    }
    window.history.replaceState(null, "", url);
  }

  async function beginNewThread() {
    setThreadStatus("connecting");
    setMessages([]);
    setAnalysisDone(false);
    setProjectInfo(null);
    setActiveConversationId(null);
    setViewingLanding(false);
    setChapterIndex(0);
    setAttachments([]);
    updateConversationUrl(null);
    try {
      const id = await startThread();
      threadIdRef.current = id;
      setActiveConversationId(id);
      setThreadStatus("ready");
      refreshConversations();
    } catch (err) {
      setThreadStatus("failed");
      setMessages([
        { id: nextId++, kind: "error", text: `会话启动失败：${err instanceof Error ? err.message : String(err)}` },
      ]);
    }
  }

  function chapterTitleAt(index?: number): string | undefined {
    return index === undefined ? undefined : chaptersRef.current[index]?.title;
  }

  /** 一轮结束后气泡显示什么：提问轮显示答复并挂上问题；写了正文的轮次显示"已生成"提示
   *  （正文在文档预览里）；既没提问也没写正文的轮次（如语言不支持的答复）原样显示答复，
   *  不能误报"已生成完成"。过程信息始终单独保留、折叠展示。 */
  function finishedAssistant(m: Extract<Msg, { kind: "assistant" }>, result: StreamMessageResult): Msg {
    const base = { ...m, process: result.process || undefined, streaming: false };
    if (result.questions?.length) return { ...base, text: result.text, questions: result.questions, answered: false };
    if (result.chapterWritten) return { ...base, text: chapterDoneLabel(chapterTitleAt(result.chapterIndex), result.revising) };
    return { ...base, text: result.text.trim() || "本轮没有生成新的正文。" };
  }

  // 本轮写哪一章是服务端在意图判定完成后才定下来的，所以状态提示与左侧章节高亮
  // 都要等这个进度回调来更新，不能在发送时就按本地 chapterIndex 猜。
  function applyTurnProgress(assistantId: number, p: TurnProgress) {
    if (p.chapterIndex !== undefined) setChapterIndex(p.chapterIndex);
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantId && m.kind === "assistant"
          ? { ...m, text: chapterGeneratingLabel(chapterTitleAt(p.chapterIndex), p.revising), process: p.process }
          : m
      )
    );
  }

  // 生成现在完全由服务端驱动，与发起请求的那次连接解耦：页面刷新/重新打开某个会话后，
  // 需要主动去问一下服务端这个会话当前是否还有生成/分析在后台进行，并接着轮询，而不是
  // 想当然地认为"上次没等到结果=生成已经停了"。
  async function resumeActiveTurn(threadId: string, hadProjectInfo: boolean) {
    try {
      const status = await getTurnStatus(threadId);
      if (status.status === "streaming") {
        const assistantId = nextId++;
        setMessages((prev) => [
          ...prev,
          { id: assistantId, kind: "assistant", text: chapterGeneratingLabel(undefined, false), streaming: true },
        ]);
        setBusy(true);
        try {
          // 聊天区不再逐字回显正文，只跟随服务端的章节判定更新状态提示。
          const result = await watchTurn(threadId, (p) => {
            applyTurnProgress(assistantId, p);
          });
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId && m.kind === "assistant" ? finishedAssistant(m, result) : m))
          );
          if (result.chapterIndex !== undefined) setChapterIndex(result.chapterIndex);
          if (result.completedChapters) setCompletedChapters(result.completedChapters);
          refreshConversations();
          if (!hadProjectInfo) await runProjectAnalysis(threadId);
        } catch (err) {
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
          setMessages((prev) => [
            ...prev,
            { id: nextId++, kind: "error", text: err instanceof Error ? err.message : "生成失败" },
          ]);
        } finally {
          setBusy(false);
        }
        return;
      }

      if (!hadProjectInfo && status.projectAnalysis) {
        if (status.projectAnalysis.status === "pending") {
          await runProjectAnalysis(threadId);
        } else if (status.projectAnalysis.status === "done" && status.projectAnalysis.info) {
          setProjectInfo(status.projectAnalysis.info);
          setAnalysisDone(true);
        } else if (status.projectAnalysis.status === "failed") {
          setAnalysisNotice(status.projectAnalysis.error ?? "未能分析出有效的项目信息，请重新开始对话");
          await beginNewThread();
        }
      }
    } catch {
      // 探测失败时静默忽略，不影响用户继续发新消息
    }
  }

  async function loadConversation(id: string) {
    try {
      const { conversation, messages: history, awaitingAnswers, completedChapters: done } = await getConversation(id);
      setCompletedChapters(done ?? []);
      const lastAssistant = history.map((m) => m.role).lastIndexOf("assistant");
      const restored: Msg[] = history.map((m, i) => {
        if (m.role === "user") {
          const { text, attachments: names } = splitStoredUserText(m.content);
          return { id: nextId++, kind: "user", text, attachments: names };
        }
        const process = m.process ?? undefined;
        // 提问轮：保留问题本身；只有仍在等待作答的那一轮才是可交互的，历史轮次置灰。
        if (m.questions?.length) {
          return {
            id: nextId++,
            kind: "assistant",
            text: m.content,
            process,
            questions: m.questions,
            answered: !(awaitingAnswers && i === lastAssistant),
          };
        }
        // 写了正文的轮次指向文档预览；没写正文、只是回复了些什么的轮次原样显示答复。
        // 过程信息与答复分开存储之前的旧消息（process 为 null）里答复混着过程与正文，保持原来的占位提示。
        if (m.chapter_written || m.process == null || !m.content.trim()) {
          return { id: nextId++, kind: "assistant", text: ASSISTANT_HISTORY_PLACEHOLDER, process };
        }
        return { id: nextId++, kind: "assistant", text: m.content, process };
      });
      threadIdRef.current = id;
      setActiveConversationId(id);
      updateConversationUrl(id);
      setChapterIndex(conversation.chapter_index ?? 0);
      setMessages(restored);
      const hadProjectInfo = !!(conversation.project_badge && conversation.project_name);
      if (hadProjectInfo) {
        setProjectInfo({ badge: conversation.project_badge as string, name: conversation.project_name as string });
        setAnalysisDone(true);
      } else {
        setProjectInfo(null);
        setAnalysisDone(restored.some((m) => m.kind === "assistant" && m.text));
      }
      setThreadStatus("ready");
      setViewingLanding(false);
      void resumeActiveTurn(id, hadProjectInfo);
    } catch (err) {
      setThreadStatus("failed");
      setMessages([
        { id: nextId++, kind: "error", text: `加载历史对话失败：${err instanceof Error ? err.message : String(err)}` },
      ]);
    }
  }

  async function handleDeleteConversation(id: string) {
    if (!confirm("确定删除这条历史对话吗？此操作无法撤销。")) return;
    try {
      await deleteConversation(id);
      refreshConversations();
      if (id === activeConversationId) {
        await beginNewThread();
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "删除失败");
    }
  }

  async function handleDeleteAllConversations() {
    if (!confirm("确定清空全部历史对话吗？此操作无法撤销。")) return;
    try {
      await deleteAllConversations();
      setConversations([]);
      await beginNewThread();
    } catch (err) {
      alert(err instanceof Error ? err.message : "清空失败");
    }
  }

  useEffect(() => {
    document.title = settings.siteName || document.title;
  }, [settings.siteName]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  useEffect(() => {
    getChapters().then(setChapters);
    getSettings().then(setSettings);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const list = await listConversations();
        if (cancelled) return;
        setConversations(list);
        const requestedId = new URLSearchParams(window.location.search).get("conversation");
        if (requestedId && list.some((c) => c.id === requestedId)) {
          await loadConversation(requestedId);
        } else {
          await beginNewThread();
        }
      } catch {
        if (!cancelled) await beginNewThread();
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const typed = inputValue.trim();
    if ((!typed && attachments.length === 0) || busy) return;
    setAnalysisNotice(null);
    setViewingLanding(false);

    const sendText = composeSendText(typed, attachments);
    const attachmentNames = attachments.map((a) => a.name);

    setInputValue("");
    setAttachments([]);
    await runTurn(sendText, { text: typed, attachments: attachmentNames });
  }

  /** 发起一轮生成：先把用户气泡挂上去，再等服务端判定章节 / 产出正文或新一轮提问。
   *  composer 与提问表单共用这条路径。 */
  async function runTurn(sendText: string, userBubble: { text: string; attachments?: string[] }) {
    const threadId = threadIdRef.current;
    if (!threadId) return;
    const isFirstExchange = messages.length === 0;
    setMessages((prev) => [
      ...prev,
      { id: nextId++, kind: "user", text: userBubble.text, attachments: userBubble.attachments },
    ]);

    // 这一轮到底是"写下一章"还是"改当前章"，要等服务端做完意图判定才知道，
    // 所以先挂一个不带章节名的占位提示，由 applyTurnProgress 补上。
    const assistantId = nextId++;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, kind: "assistant", text: chapterGeneratingLabel(undefined, false), streaming: true },
    ]);
    setBusy(true);

    try {
      const result = await streamMessage(threadId, sendText, (p) => {
        applyTurnProgress(assistantId, p);
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId && m.kind === "assistant" ? finishedAssistant(m, result) : m))
      );
      if (result.chapterIndex !== undefined) setChapterIndex(result.chapterIndex);
      if (result.completedChapters) setCompletedChapters(result.completedChapters);
      refreshConversations();
      if (isFirstExchange && !analysisDone) {
        void runProjectAnalysis(threadId);
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      setMessages((prev) => [
        ...prev,
        { id: nextId++, kind: "error", text: err instanceof Error ? err.message : "生成失败" },
      ]);
    } finally {
      setBusy(false);
    }
  }

  /** 提交提问表单：把该轮问题置为已作答，然后按普通一轮发回给模型。 */
  function submitAnswers(assistantId: number, answerText: string) {
    if (busy) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === assistantId && m.kind === "assistant" ? { ...m, answered: true } : m))
    );
    void runTurn(answerText, { text: answerText });
  }

  /** 管理员在章节弹窗的「提示词」页保存后，同步刷新本地章节列表，避免关掉再打开还是旧内容。 */
  function handlePromptSaved(chapterNum: string, prompt: string) {
    setChapters((prev) => prev.map((c) => (c.num === chapterNum ? { ...c, prompt } : c)));
    setDocModalChapter((prev) => (prev && prev.num === chapterNum ? { ...prev, prompt } : prev));
  }

  const composerEnabled = threadStatus === "ready" && !busy;
  const docsEnabled = !!activeConversationId && messages.some((m) => m.kind === "user");
  const allChaptersDone = chapters.length > 0 && chapters.every((c) => completedChapters.includes(c.num));
  const exportEnabled = !!activeConversationId && completedChapters.length > 0;

  async function handleFiles(files: FileList | File[]) {
    const file = Array.from(files)[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    const isDocx =
      file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.name.toLowerCase().endsWith(".docx");
    try {
      const content = isPdf ? await extractPdfText(file) : isDocx ? await extractDocxText(file) : await file.text();
      // 文件正文不进输入框：只作为一个附件挂在composer上，聊天区和输入框里都只显示文件名。
      setAttachments((prev) => [...prev, { id: attachmentIdRef.current++, name: file.name, content }]);
      if (!sessionLoggedRef.current) {
        sessionLoggedRef.current = true;
        logEvent("session_start", `${user.username} 开始了一次新对话`);
      }
      logEvent("upload", `${user.username} 上传了文件「${file.name}」`);
    } catch (err) {
      setAnalysisNotice(`文件「${file.name}」解析失败：${err instanceof Error ? err.message : "未知错误"}`);
    }
  }

  function removeAttachment(id: number) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (composerEnabled) setDragActive(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    if (!composerEnabled) return;
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }

  if (!started) {
    return (
      <div
        className="landing"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="landing-user">
          <button className="btn btn-text landing-history-btn" type="button" onClick={() => setHistoryModalOpen(true)}>
            <History size={14} className="icon" />
            <span>历史对话</span>
          </button>
          <AccountMenu user={user} onLogout={onLogout} onUpdateUser={onUpdateUser} />
        </div>

        {analysisNotice && (
          <div className="landing-notice" role="alert">
            <span>{analysisNotice}</span>
            <button className="btn btn-text" type="button" onClick={() => setAnalysisNotice(null)}>
              <X size={14} />
            </button>
          </div>
        )}

        <div className={`landing-card ${dragActive ? "drag-active" : ""}`}>
          <span className="logo-mark">临床方案平台</span>
          <h1 className="landing-title">开始构建您的临床试验方案</h1>
          <p className="landing-subtitle">拖拽需求文档到此处，或直接输入信息，开始生成方案</p>

          <form className="composer landing-composer" onSubmit={handleSubmit}>
            <textarea
              className="composer-input"
              rows={3}
              placeholder={
                threadStatus === "connecting"
                  ? "正在连接 Codex…"
                  : threadStatus === "failed"
                    ? "会话连接失败"
                    : "输入需求信息，或拖拽文档到此处…"
              }
              disabled={!composerEnabled}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  (e.currentTarget.form as HTMLFormElement)?.requestSubmit();
                }
              }}
            />
            <button
              className="btn btn-clay composer-send"
              type="submit"
              disabled={!composerEnabled || (!inputValue.trim() && attachments.length === 0)}
            >
              <Send size={16} className="icon" />
              <span>发送</span>
            </button>
          </form>

          {attachments.length > 0 && (
            <ul className="attachment-chips">
              {attachments.map((a) => (
                <li className="attachment-chip" key={a.id}>
                  <Paperclip size={12} className="icon" />
                  <span>{a.name}</span>
                  <button
                    className="attachment-chip-remove"
                    type="button"
                    disabled={!composerEnabled}
                    onClick={() => removeAttachment(a.id)}
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label className="landing-upload">
            <UploadCloud size={14} className="icon" />
            <span>或选择文件上传</span>
            <input
              type="file"
              hidden
              disabled={!composerEnabled}
              onChange={(e) => {
                if (e.target.files?.length) handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </label>

          {threadStatus === "failed" && (
            <p className="inline-hint">会话连接失败，请刷新页面重试</p>
          )}
        </div>

        {historyModalOpen && (
          <HistoryModal
            conversations={conversations}
            activeConversationId={activeConversationId}
            onClose={() => setHistoryModalOpen(false)}
            onSelect={(id) => {
              setHistoryModalOpen(false);
              loadConversation(id);
            }}
            onDelete={handleDeleteConversation}
            onDeleteAll={handleDeleteAllConversations}
          />
        )}
      </div>
    );
  }

  return (
    <div className="app">
      <Sidebar
        analysisDone={analysisDone}
        analyzing={analyzing}
        projectInfo={projectInfo}
        chapters={chapters}
        completedChapters={completedChapters}
        started={started}
        chapterIndex={chapterIndex}
        docsEnabled={docsEnabled}
        allChaptersDone={allChaptersDone}
        exportFormat={exportFormat}
        setExportFormat={setExportFormat}
        exportEnabled={exportEnabled}
        activeConversationId={activeConversationId}
        onOpenChapterDoc={(chapter) => setDocModalChapter(chapter)}
        onOpenFullDoc={() => setDocModalOpen(true)}
        onBackToLanding={() => {
          setViewingLanding(true);
          updateConversationUrl(null);
        }}
      />

      {historyModalOpen && (
        <HistoryModal
          conversations={conversations}
          activeConversationId={activeConversationId}
          onClose={() => setHistoryModalOpen(false)}
          onSelect={(id) => {
            setHistoryModalOpen(false);
            if (id !== activeConversationId) loadConversation(id);
          }}
          onDelete={handleDeleteConversation}
          onDeleteAll={handleDeleteAllConversations}
        />
      )}

      {docModalOpen && activeConversationId && (
        <DocumentEditor
          conversationId={activeConversationId}
          chapters={chapters}
          user={user}
          onPromptSaved={handlePromptSaved}
          onClose={() => setDocModalOpen(false)}
        />
      )}

      {docModalChapter && activeConversationId && (
        <DocumentEditor
          conversationId={activeConversationId}
          chapters={chapters}
          chapter={docModalChapter}
          user={user}
          onPromptSaved={handlePromptSaved}
          onClose={() => setDocModalChapter(null)}
        />
      )}

      <main className="chat-main">
        <header className="chat-topbar">
          <div>
            <h1 className="chat-title">对话</h1>
            <p className="chat-subtitle">与 Codex 实时对话</p>
          </div>
          <AccountMenu user={user} onLogout={onLogout} onUpdateUser={onUpdateUser} />
        </header>

        <div className="chat-scroll" ref={scrollRef}>
          {messages.map((m) => {
            if (m.kind === "user") {
              return (
                <div className="msg msg-user" key={m.id}>
                  <div className="bubble bubble-user">
                    {m.text}
                    {m.attachments?.map((name) => (
                      <span className="bubble-attachment" key={name}>
                        <Paperclip size={13} className="icon" />
                        <span>{name}</span>
                      </span>
                    ))}
                  </div>
                  <div className="avatar avatar-user">你</div>
                </div>
              );
            }
            if (m.kind === "error") {
              return (
                <div className="msg msg-assistant" key={m.id}>
                  <div className="avatar avatar-assistant">!</div>
                  <div className="bubble bubble-error">{m.text}</div>
                </div>
              );
            }
            return (
              <div className="msg msg-assistant" key={m.id}>
                <div className="avatar avatar-assistant">AI</div>
                <div className="bubble">
                  {m.process ? (
                    // 过程信息与结果分开：生成中展开以便看到进度，结束后默认收起。
                    <details className="bubble-process" open={!!m.streaming}>
                      <summary>{m.streaming ? "处理过程（进行中）" : "处理过程"}</summary>
                      <div className="bubble-process-body">{m.process}</div>
                    </details>
                  ) : null}
                  {m.text}
                  {m.streaming && <TypingDots />}
                  {m.questions?.length ? (
                    <QuestionForm
                      questions={m.questions}
                      disabled={!!m.answered || busy}
                      onSubmit={(text) => submitAnswers(m.id, text)}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <form className="composer" onSubmit={handleSubmit}>
          <textarea
            className="composer-input"
            rows={1}
            placeholder={
              threadStatus === "connecting"
                ? "正在连接 Codex…"
                : threadStatus === "failed"
                  ? "会话连接失败"
                  : "输入消息…"
            }
            disabled={!composerEnabled}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                (e.currentTarget.form as HTMLFormElement)?.requestSubmit();
              }
            }}
          />
          <button className="btn btn-clay composer-send" type="submit" disabled={!composerEnabled || !inputValue.trim()}>
            <Send size={16} className="icon" />
            <span>发送</span>
          </button>
        </form>
      </main>
    </div>
  );
}
